import { getRole, hasPermission, isSuperAdmin, isUserManager } from '../utils/auth';

// Single source of truth for what an admin user can open -- the sidebar and the Home page both
// render from this, so a page can't show up in one and be missing (or wrongly shown) in the other.
// Each group only appears if at least one of its items is visible to the current role.
export const getNavGroups = () => {
  const canCalendar  = hasPermission('calendar');
  const canQuestions = hasPermission('questions');
  const canGames     = hasPermission('games');
  const canFaq       = hasPermission('faq');
  const canStudentRegistration = hasPermission('student_registration');
  // Evaluator-only item: if a user also has student_registration (e.g. super_admin/branch_admin,
  // or a custom role granted both), grade editing lives inside the Student Registration page
  // itself -- no separate entry needed, matching how admins already get one combined experience.
  const canGradeEvaluationOnly = hasPermission('grade_evaluation') && !canStudentRegistration;
  const canUsers    = isUserManager();
  const canBranches = isSuperAdmin();

  return [
    {
      key: 'student', label: 'Student Management', icon: 'student',
      items: [
        canStudentRegistration && { page: 'admin-student-registration', label: 'Student Registration', icon: 'userPlus', desc: 'Register students, track fees and volunteering, and manage book distribution.' },
        canGradeEvaluationOnly && { page: 'admin-grade-evaluation', label: 'Grade Evaluation', icon: 'award', desc: 'Assign a grade to students awaiting evaluation or promotion.' },
        canUsers && { page: 'admin-transfer-requests', label: 'Transfer Requests', icon: 'repeat', desc: 'Review and approve student transfer requests.' },
      ].filter(Boolean),
    },
    {
      key: 'volunteer', label: 'Volunteer Management', icon: 'volunteer',
      items: [
        canUsers && { page: 'admin-volunteers', label: 'Volunteers', icon: 'volunteer', desc: 'Manage volunteers and assign main and sub teachers to each class.' },
      ].filter(Boolean),
    },
    {
      key: 'users', label: 'User Management', icon: 'users',
      items: [
        canUsers && { page: 'admin-users', label: 'Manage Users', icon: 'users', desc: 'Add and manage admin users and their roles.' },
        canBranches && { page: 'admin-role-permissions', label: 'Role Permissions', icon: 'shield', desc: 'Configure which pages each role can access.' },
      ].filter(Boolean),
    },
    {
      key: 'branch', label: 'Branch Management', icon: 'branchTree',
      items: [
        canBranches && { page: 'admin-branches', label: 'Manage Branches', icon: 'branchTree', desc: 'Manage school branches and locations.' },
        canBranches && { page: 'admin-site-pages', label: 'Site Pages', icon: 'globe', desc: 'Edit the pages shown on the public website.' },
        canBranches && { page: 'admin-contact-us', label: 'Contact Us Page', icon: 'phone', desc: 'Edit the public Contact Us page.' },
      ].filter(Boolean),
    },
    {
      key: 'calendar', label: 'Calendar Management', icon: 'calendar',
      items: [
        canCalendar && { page: 'admin-calendar', label: 'Manage Calendar', icon: 'calendar', desc: 'Manage school calendar events, holidays, and special dates.' },
      ].filter(Boolean),
    },
    {
      key: 'email', label: 'Email Management', icon: 'mail',
      items: [
        canUsers && { page: 'admin-email-templates', label: 'Email Templates', icon: 'fileText', desc: 'Edit the automatic emails the system sends.' },
        canUsers && { page: 'admin-bulk-email', label: 'Bulk Email', icon: 'send', desc: 'Email the parents or teachers of selected sections.' },
      ].filter(Boolean),
    },
    {
      key: 'game', label: 'Game Management', icon: 'gameSymbols',
      items: [
        canQuestions && { page: 'admin-question-groups', label: 'Question Groups', icon: 'help', desc: 'Create and manage trivia question sets for the game.' },
        canGames && { page: 'admin-sessions', label: 'Game Sessions', icon: 'playCircle', desc: 'View active and past game sessions across your branch.' },
        canGames && { page: 'admin-game-create', label: 'Create Game', icon: 'plusCircle', desc: 'Set up a new trivia game session.' },
      ].filter(Boolean),
    },
    {
      key: 'faq', label: 'FAQ', icon: 'faq',
      items: [
        canFaq && { page: 'admin-faq', label: 'FAQ Management', icon: 'faq', desc: 'Manage the FAQ page shown to students and parents.' },
      ].filter(Boolean),
    },
  ].filter(group => group.items.length > 0);
};

// "My Class" is a standalone item rather than a group, since it's a single self-service page.
// Shown for the 'teacher' role, the only role auto-provisioned as a main volunteer assignment's
// login account (see routes/volunteers.py's _ensure_teacher_account). The page itself handles
// having zero classes gracefully, so no extra live lookup is needed just to decide visibility.
export const getStandaloneItems = () => (
  getRole() === 'teacher'
    ? [{ page: 'admin-my-class', label: 'My Class', icon: 'book', desc: "Email the parents of the class you teach." }]
    : []
);
